/**
 * Generador de variaciones de casos clínicos.
 *
 * Parte de los 3 escenarios base (SCENARIO_IAM/SEPSIS/EPOC) y genera variantes
 * con edad, sexo y vitales distintos pero clínicamente coherentes: la
 * troponina en IAM sigue siempre elevada, el lactato en sepsis sigue crítico,
 * el pH en EPOC sigue ácido. Es decir, la patología se mantiene — solo cambia
 * el "sabor" del paciente para que el alumno no memorice valores.
 *
 * Seed opcional para reproducibilidad (útil en exámenes: "resuelve el caso
 * con seed 42 y comparemos tu razonamiento").
 */

import { ClinicalScenario, SCENARIOS, SCENARIO_IAM, SCENARIO_SEPSIS, SCENARIO_EPOC } from './case-scenarios';

// ─── SeededRandom: LCG simple y determinista ─────────────────────────────────

class SeededRandom {
  private seed: number;
  constructor(seed?: number) {
    this.seed = (seed ?? Date.now()) % 2147483647;
    if (this.seed <= 0) this.seed += 2147483646;
  }
  next(): number {
    this.seed = (this.seed * 16807) % 2147483647;
    return (this.seed - 1) / 2147483646;
  }
  intBetween(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }
  floatBetween(min: number, max: number, decimals = 1): number {
    const v = this.next() * (max - min) + min;
    const m = Math.pow(10, decimals);
    return Math.round(v * m) / m;
  }
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
}

// ─── Plantillas de rango por caso ────────────────────────────────────────────

type NumericMutation = {
  nodeId: string;
  range: [number, number];
  decimals?: number;
};

interface CaseTemplate {
  base: ClinicalScenario;
  ageRange: [number, number];
  sexBias: 'male' | 'female' | 'balanced';
  initialVitals: NumericMutation[];  // mutaciones en los vitales iniciales
  labMutations: Array<{ examId: string; targetNodeId: string; range: [number, number]; decimals?: number }>;
}

const TEMPLATES: Record<string, CaseTemplate> = {
  iam: {
    base: SCENARIO_IAM,
    ageRange: [50, 85],
    sexBias: 'male',       // IAM más frecuente en varones en este rango
    initialVitals: [
      { nodeId: 'vital-fc',   range: [95, 135] },
      { nodeId: 'vital-pa',   range: [78, 145] },
      { nodeId: 'vital-fr',   range: [18, 28] },
      { nodeId: 'vital-spo2', range: [92, 97] },
    ],
    labMutations: [
      { examId: 'exam-troponin', targetNodeId: 'lab-trop', range: [120, 2500] },
    ],
  },
  sepsis: {
    base: SCENARIO_SEPSIS,
    ageRange: [55, 85],
    sexBias: 'balanced',
    initialVitals: [
      { nodeId: 'vital-1',    range: [70, 100] },        // PA sistólica
      { nodeId: 'vital-2',    range: [105, 140] },       // FC
      { nodeId: 'vital-fr',   range: [22, 32] },
      { nodeId: 'vital-temp', range: [38.5, 40.2], decimals: 1 },
    ],
    labMutations: [
      { examId: 'exam-lactate',  targetNodeId: 'lab-1', range: [3.0, 6.5], decimals: 1 },
      { examId: 'exam-hemogram', targetNodeId: 'lab-2', range: [14000, 26000] },
    ],
  },
  epoc: {
    base: SCENARIO_EPOC,
    ageRange: [55, 80],
    sexBias: 'male',
    initialVitals: [
      { nodeId: 'vital-1',  range: [24, 34] },    // FR
      { nodeId: 'vital-2',  range: [82, 90] },    // SpO2
      { nodeId: 'vital-fc', range: [100, 125] },
      { nodeId: 'vital-pa', range: [125, 165] },
    ],
    labMutations: [
      { examId: 'exam-gases', targetNodeId: 'lab-1', range: [7.20, 7.33], decimals: 2 },
      { examId: 'exam-gases', targetNodeId: 'lab-2', range: [55, 80] },  // PaCO2
    ],
  },
};

// ─── Generador principal ─────────────────────────────────────────────────────

function cloneScenario(s: ClinicalScenario): ClinicalScenario {
  return JSON.parse(JSON.stringify(s)) as ClinicalScenario;
}

function mutateNodeValue(scenario: ClinicalScenario, nodeId: string, newValue: number): void {
  for (const wave of scenario.waves) {
    const node = wave.nodes.find(n => n.id === nodeId);
    if (node?.valueQuantity) {
      node.valueQuantity.value = newValue;
      return;
    }
  }
  for (const exam of scenario.orderableExams) {
    const node = exam.nodes.find(n => n.id === nodeId);
    if (node?.valueQuantity) {
      node.valueQuantity.value = newValue;
      return;
    }
  }
}

/**
 * Genera un caso clínicamente coherente a partir de una plantilla base.
 * Registra el escenario generado en SCENARIOS bajo un id nuevo y lo devuelve.
 * El alumno carga el caso por ese id como cualquier otro escenario.
 */
export function generateCase(base: 'iam' | 'sepsis' | 'epoc', seed?: number): string {
  const template = TEMPLATES[base];
  if (!template) throw new Error(`Unknown case template: ${base}`);

  const rng = new SeededRandom(seed);
  const scenario = cloneScenario(template.base);

  // Identidad del paciente
  scenario.demographics.age = rng.intBetween(template.ageRange[0], template.ageRange[1]);
  scenario.demographics.sex =
    template.sexBias === 'balanced'
      ? rng.pick(['male', 'female'] as const)
      : rng.next() < 0.8 ? template.sexBias : (template.sexBias === 'male' ? 'female' : 'male');

  // Vitales iniciales
  for (const mut of template.initialVitals) {
    const v = rng.floatBetween(mut.range[0], mut.range[1], mut.decimals ?? 0);
    mutateNodeValue(scenario, mut.nodeId, v);
  }

  // Resultados de laboratorio (aparecen al ordenar el examen)
  for (const mut of template.labMutations) {
    const v = rng.floatBetween(mut.range[0], mut.range[1], mut.decimals ?? 0);
    mutateNodeValue(scenario, mut.targetNodeId, v);
  }

  // Reemplaza edad y género en la narrativa de presentación. Buscamos los
  // valores hardcoded del template base para que "Hombre de 67 años" pase
  // a "Mujer de 71 años" según la generación. Cuando la substitución no
  // encuentra el patrón exacto, la narrativa queda con los valores base
  // (no rompe, solo queda desincronizada — aceptable como fallback).
  const baseAge = template.base.demographics.age;
  const baseSex = template.base.demographics.sex === 'male' ? 'Hombre' : 'Mujer';
  const newSex = scenario.demographics.sex === 'male' ? 'Hombre' : 'Mujer';
  scenario.presentation = scenario.presentation
    .replace(new RegExp(`${baseSex} de ${baseAge} años`, 'g'), `${newSex} de ${scenario.demographics.age} años`)
    .replace(new RegExp(`de ${baseAge} años`, 'g'), `de ${scenario.demographics.age} años`);

  // Id único y registro. El alumno verá "IAM #3847" en la UI.
  const shortSeed = Math.abs((rng as unknown as { seed: number }).seed) % 10000;
  scenario.id = `${base}-gen-${shortSeed}`;
  scenario.title = `${template.base.title} (variación #${shortSeed})`;
  SCENARIOS[scenario.id] = scenario;

  return scenario.id;
}

// Re-export para que el import en main.ts sea más corto
export { SCENARIO_IAM, SCENARIO_SEPSIS, SCENARIO_EPOC };
